import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://mrmlettings.co.uk';
const DEFAULT_IMAGE = `${SITE_URL}/media/hero-property-poster.jpg`;

const pages = {
  '/': ['Estate & Letting Agents in Middlesbrough | MRM Lettings', 'Independent Middlesbrough estate agents for property sales, lettings and management across Teesside. Search available homes or request a valuation.'],
  '/properties': ['Properties for Sale & Rent in Teesside | MRM Lettings', 'Explore properties for sale and to rent across Middlesbrough, Teesside and the surrounding area with MRM Lettings.'],
  '/buyers': ['Buying Property in Middlesbrough & Teesside | MRM Lettings', 'Local guidance for property buyers across Middlesbrough and Teesside, from finding a home through to completion.'],
  '/sellers': ['Sell Your Property in Middlesbrough | MRM Lettings', 'Sell your Middlesbrough or Teesside property with clear local advice, professional marketing and dependable support.'],
  '/landlords': ['Landlord & Property Management Services | MRM Lettings', 'Let and manage your property with a dedicated Middlesbrough team supporting landlords across Teesside.'],
  '/tenants': ['Homes to Rent in Middlesbrough & Teesside | MRM Lettings', 'Find homes to rent and get straightforward tenancy support from MRM Lettings in Middlesbrough and Teesside.'],
  '/about': ['About Our Family-Led Estate Agency | MRM Lettings', 'Discover the story of MRM Lettings, a family-led Middlesbrough property company founded in 2019 by Mohammad Rasheed Mahammad Riyal.'],
  '/contact': ['Contact MRM Lettings in Middlesbrough', 'Contact our estate and letting agency at 142 Victoria Road, Middlesbrough for property sales, lettings, management and valuations.'],
  '/repairs': ['Report a Property Repair | MRM Lettings', 'Report a maintenance issue for a property managed by MRM Lettings and tell our Middlesbrough team what support you need.'],
  '/sellers/valuation': ['Book a Property Valuation in Middlesbrough | MRM Lettings', 'Request a property valuation from local Middlesbrough estate agents with knowledge of the Teesside sales and lettings market.'],
  '/payments': ['Make a Payment | MRM Lettings', 'Access payment information for MRM Lettings customers.'],
  '/privacy': ['Privacy Policy | MRM Lettings', 'Read how MRM Lettings collects, uses and protects personal information.'],
  '/terms': ['Website Terms | MRM Lettings', 'Read the terms that apply when using the MRM Lettings website.'],
};

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

function setCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
}

export function updatePageSeo({ title, description, path, image = DEFAULT_IMAGE, type = 'website', noIndex = false }) {
  const canonical = path.startsWith('http') ? path : `${SITE_URL}${path === '/' ? '/' : path}`;
  const socialImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  document.title = title;
  setCanonical(canonical);
  setMeta('meta[name="description"]', { name: 'description', content: description });
  setMeta('meta[name="robots"]', { name: 'robots', content: noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' });
  setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
  setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
  setMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
  setMeta('meta[property="og:type"]', { property: 'og:type', content: type });
  setMeta('meta[property="og:image"]', { property: 'og:image', content: socialImage });
  setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
  setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: socialImage });
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isProperty = pathname.startsWith('/property/');
    const isArticle = pathname.startsWith('/news/');
    const canonicalPath = pathname === '/privacy-policy' || pathname === '/cookies' ? '/privacy' : pathname;
    const [title, description] = pages[pathname]
      || (isProperty
        ? ['Property Details | MRM Lettings', 'View property details, photographs and enquiry information from MRM Lettings.']
        : isArticle
          ? ['Property News & Advice | MRM Lettings', 'Read local property news, market insight and practical guidance from MRM Lettings.']
          : ['MRM Lettings | Middlesbrough Estate Agents', 'Property sales, lettings and management across Middlesbrough and Teesside.']);
    const noIndex = pathname.startsWith('/admin') || pathname === '/payments';

    updatePageSeo({ title, description, path: canonicalPath, type: isArticle ? 'article' : 'website', noIndex });
  }, [pathname]);

  return null;
}
